import React, { useState } from "react";
import { Mail, Phone, Crown, ShieldCheck, ExternalLink, CheckCircle2, BellRing } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FEATURES, hasFeature } from "../lib/perms";
import { ChannelToggles } from "../components/ChannelToggles";
import { enablePush } from "../lib/push";
import { PageHeader } from "../components/Layout";
import { formatDateBR } from "../lib/format";

export default function Profile() {
    const { user, refresh } = useAuth();
    const [prefs, setPrefs] = useState(user?.notif_prefs || { email: true, whatsapp: false, sms: false, push: false });
    if (!user) return null;
    const initials = (user.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
    const allowed = FEATURES.filter((f) => hasFeature(user, f.key));

    // Opens the access management page in a new browser tab.
    const openAccessPage = () => window.open("/acessos", "_blank", "noopener");

    const savePref = async (key, val) => {
        const next = { ...prefs, [key]: val };
        setPrefs(next);
        try { await api.put("/users/me/notif-prefs", next); refresh(); }
        catch (e) { toast.error("Erro ao salvar preferências"); }
    };
    const activatePush = async () => {
        try {
            await enablePush();
            const next = { ...prefs, push: true };
            setPrefs(next);
            await api.put("/users/me/notif-prefs", next);
            refresh();
            toast.success("Push ativado neste dispositivo");
        } catch (e) { toast.error(e.message || "Falha ao ativar push"); }
    };

    return (
        <div>
            <PageHeader overline="Minha conta" title="Meu perfil" description="Seus dados e as funções que você pode acessar no sistema." />

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="rounded-2xl border border-border bg-white p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] lg:col-span-1" data-testid="profile-card">
                    <div className="flex flex-col items-center text-center">
                        {user.picture ? (
                            <img src={user.picture} alt="" className="h-20 w-20 rounded-2xl object-cover" />
                        ) : (
                            <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-accent/15 text-2xl font-semibold text-accent">{initials}</div>
                        )}
                        <h3 className="mt-4 font-heading text-xl font-medium">{user.name}</h3>
                        <span className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${user.is_master ? "bg-secondary/50 text-secondary-foreground" : "bg-primary/10 text-primary"}`}>
                            {user.is_master ? <Crown className="h-3.5 w-3.5" /> : null}
                            {user.is_master ? "Master Geral" : `${user.role} · ${user.nivel}`}
                        </span>
                    </div>
                    <div className="mt-6 space-y-2 border-t border-border/60 pt-4 text-sm text-muted-foreground">
                        <p className="flex items-center gap-2"><Mail className="h-4 w-4" /> {user.email}</p>
                        {user.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4" /> {user.phone}</p>}
                        {user.created_at && <p className="text-xs">Membro desde {formatDateBR(user.created_at)}</p>}
                    </div>
                </div>

                <div className="lg:col-span-2">
                    {hasFeature(user, "gestao_acessos") && (
                        <div className="mb-6 flex flex-col gap-4 rounded-2xl border border-accent/30 bg-accent/5 p-6 shadow-[0_0_15px_rgba(140,168,153,0.15)] sm:flex-row sm:items-center sm:justify-between" data-testid="access-page-cta">
                            <div className="flex items-center gap-3">
                                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/15 text-accent"><ShieldCheck className="h-5 w-5" /></div>
                                <div>
                                    <p className="font-heading text-lg font-medium">Gestão de Acessos</p>
                                    <p className="text-sm text-muted-foreground">Controle o que cada usuário pode acessar.</p>
                                </div>
                            </div>
                            <button onClick={openAccessPage} data-testid="open-access-page-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-white transition-transform hover:-translate-y-0.5">
                                Abrir em nova página <ExternalLink className="h-4 w-4" />
                            </button>
                        </div>
                    )}

                    <div className="rounded-2xl border border-border bg-white p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]" data-testid="profile-permissions">
                        <h3 className="font-heading text-xl font-medium">Minhas funções liberadas</h3>
                        <p className="mb-4 text-sm text-muted-foreground">Você tem acesso às seguintes áreas do sistema.</p>
                        {allowed.length === 0 ? (
                            <div className="rounded-xl border border-dashed border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground" data-testid="profile-pending">
                                Seu cadastro foi criado e está aguardando o Master Geral liberar seu nível de acesso.
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {allowed.map((f) => (
                                    <div key={f.key} className="flex items-center gap-3 rounded-xl border border-border/70 bg-muted/30 px-4 py-3">
                                        <f.icon className="h-4 w-4 text-primary" />
                                        <span className="flex-1 text-sm font-medium">{f.label}</span>
                                        <CheckCircle2 className="h-4 w-4 text-primary" />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="mt-6 rounded-2xl border border-border bg-white p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]" data-testid="notif-prefs-card">
                        <h3 className="font-heading text-xl font-medium">Notificações</h3>
                        <p className="mb-4 text-sm text-muted-foreground">Escolha como deseja ser avisado sobre ponto, relatórios, lançamentos e avisos.</p>
                        <ChannelToggles prefs={prefs} onToggle={savePref} />
                        <button onClick={activatePush} data-testid="enable-push-btn" className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-white transition-transform hover:-translate-y-0.5">
                            <BellRing className="h-4 w-4" /> Ativar push neste dispositivo
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
